import { Link, useFetcher } from "@remix-run/react"
import { formatDistanceToNow } from "date-fns"
import { useEffect, useRef, useState, type FC, type ReactNode } from "react"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import ContentAgreementModal from "#app/components/ui/content-agreement-modal.js"
import { reactionTypes } from "#app/components/ui/reactions.js"

type CommentReaction = {
	id: string
	type: string
	userId: string | null
}

type Comment = {
	id: string
	text: string
	createdAt: string
	user: {
		id: string
		username: string
		image: { id: string } | null
	} | null
	reactions: CommentReaction[]
}

interface CommentsProps {
	comments: Comment[]
	contentType: "script" | "guide" | "build" | "fanSite" | "botScriptBuild" | "news"
	contentId: string
	currentUserId: string | null
	isAdmin?: boolean
	isMuted?: boolean
	hasMore?: boolean
	nextCursor?: string | null
	hasAgreedToTerms?: boolean
	/** When false, the composer is replaced by `gateMessage` (e.g. a paid/beta perk). Default true. */
	canComment?: boolean
	/** Shown in place of the composer when `canComment` is false. */
	gateMessage?: ReactNode
	/** When true, skip the shared daily-interaction limit UI (this surface's commenters are unlimited). */
	hideDailyLimit?: boolean
}

function CommentReactions({
	commentId,
	reactions,
	currentUserId,
}: {
	commentId: string
	reactions: CommentReaction[]
	currentUserId: string | null
}) {
	const fetcher = useFetcher()

	const userReactionTypes = new Set(
		reactions.filter((r) => r.userId === currentUserId).map((r) => r.type),
	)

	// Group reactions by type with counts
	const counts: Record<string, number> = {}
	for (const r of reactions) {
		counts[r.type] = (counts[r.type] || 0) + 1
	}

	const handleReact = (type: string) => {
		if (!currentUserId) return
		const has = userReactionTypes.has(type)
		fetcher.submit(
			{ contentType: "comment", type, commentId },
			{ method: has ? "DELETE" : "POST", action: "/reactions" },
		)
	}

	return (
		<div className="flex gap-1.5 flex-wrap">
			{reactionTypes.map((rt) => {
				const count = counts[rt.type] || 0
				const isSelected = userReactionTypes.has(rt.type)
				if (count === 0 && !currentUserId) return null
				return (
					<button
						key={rt.type}
						onClick={() => handleReact(rt.type)}
						disabled={!currentUserId}
						title={currentUserId ? rt.label : "Log in to react"}
						className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition ${
							isSelected
								? "bg-primary/10 text-primary"
								: "bg-muted-foreground/5 text-muted-foreground hover:bg-muted-foreground/10"
						} ${!currentUserId ? "cursor-default" : "cursor-pointer"}`}
					>
						<span>{rt.icon}</span>
						{count > 0 && <span>{count}</span>}
					</button>
				)
			})}
		</div>
	)
}

const Comments: FC<CommentsProps> = ({
	comments: initialComments,
	contentType,
	contentId,
	currentUserId,
	isAdmin = false,
	isMuted = false,
	hasMore: initialHasMore = false,
	nextCursor: initialNextCursor = null,
	hasAgreedToTerms = false,
	canComment = true,
	gateMessage,
	hideDailyLimit = false,
}) => {
	const fetcher = useFetcher<{ success?: boolean; error?: string }>()
	const loadMoreFetcher = useFetcher<{ comments: Comment[]; hasMore: boolean; nextCursor: string | null }>()
	const dailyStatusFetcher = useFetcher<{ allowed: boolean; remaining: number; isPremium: boolean; loggedIn: boolean }>()
	const formRef = useRef<HTMLFormElement>(null)
	const [optimisticDeletes, setOptimisticDeletes] = useState<Set<string>>(new Set())
	const [showAgreementModal, setShowAgreementModal] = useState(false)
	const [agreedLocally, setAgreedLocally] = useState(hasAgreedToTerms)
	const [extraComments, setExtraComments] = useState<Comment[]>([])
	const [hasMore, setHasMore] = useState(initialHasMore)
	const [cursor, setCursor] = useState(initialNextCursor)

	// Fetch daily status on mount and after posting a comment
	const dailyStatusLoaded = useRef(false)
	useEffect(() => {
		if (currentUserId && !dailyStatusLoaded.current) {
			dailyStatusLoaded.current = true
			dailyStatusFetcher.load('/resources/daily-status')
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUserId])

	// Refresh daily status after a successful comment post
	useEffect(() => {
		if (fetcher.state === 'idle' && fetcher.data?.success) {
			dailyStatusFetcher.load('/resources/daily-status')
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetcher.state, fetcher.data])

	const dailyStatus = dailyStatusFetcher.data
	const atLimit = !hideDailyLimit && dailyStatus?.loggedIn && !dailyStatus.isPremium && !dailyStatus.allowed

	// Append loaded comments when loadMoreFetcher completes
	const prevLoadMoreData = useRef(loadMoreFetcher.data)
	if (loadMoreFetcher.data && loadMoreFetcher.data !== prevLoadMoreData.current) {
		prevLoadMoreData.current = loadMoreFetcher.data
		setExtraComments((prev) => [...prev, ...loadMoreFetcher.data!.comments])
		setHasMore(loadMoreFetcher.data.hasMore)
		setCursor(loadMoreFetcher.data.nextCursor)
	}

	const allComments = [...initialComments, ...extraComments]

	const isSubmitting = fetcher.state === "submitting"
	const hasError = fetcher.state === "idle" && fetcher.data?.error

	const handleDelete = (commentId: string) => {
		if (!confirm("Delete this comment?")) return
		setOptimisticDeletes((prev) => new Set(prev).add(commentId))
		fetcher.submit(
			{ intent: "delete", commentId },
			{ method: "POST", action: "/resources/comments" },
		)
	}

	const visibleComments = allComments.filter((c) => !optimisticDeletes.has(c.id))

	return (
		<div>
			<h3 className="text-lg font-semibold">
				Comments ({visibleComments.length})
			</h3>

			{showAgreementModal && (
				<ContentAgreementModal
					onAgreed={() => {
						setAgreedLocally(true)
						setShowAgreementModal(false)
					}}
					onCancel={() => setShowAgreementModal(false)}
				/>
			)}

			{currentUserId && !isMuted ? (
				!canComment ? (
					<div className="mt-3 rounded-lg border border-input bg-muted-foreground/5 p-3 text-sm text-muted-foreground">
						{gateMessage ?? 'Commenting is not available to your account.'}
					</div>
				) : atLimit ? (
					<div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
						<p className="text-sm text-amber-700 dark:text-amber-400">
							You've reached your daily limit of 5 messages and comments.{' '}
							<Link to="/premium" className="font-medium underline hover:no-underline">
								Upgrade to Premium
							</Link>{' '}
							for unlimited access.
						</p>
					</div>
				) : (
					<fetcher.Form
						ref={formRef}
						method="POST"
						action="/resources/comments"
						onSubmit={(e) => {
							if (!agreedLocally) {
								e.preventDefault()
								setShowAgreementModal(true)
								return
							}
							setTimeout(() => formRef.current?.reset(), 0)
						}}
						className="mt-3"
					>
						<HoneypotInputs />
						<input type="hidden" name="intent" value="create" />
						<input type="hidden" name="contentType" value={contentType} />
						<input type="hidden" name="contentId" value={contentId} />
						<textarea
							name="text"
							required
							maxLength={2000}
							placeholder="Write a comment..."
							className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
							rows={3}
						/>
						<div className="mt-2 flex items-center gap-3">
							<button
								type="submit"
								disabled={isSubmitting}
								className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
							>
								{isSubmitting ? "Posting..." : "Post Comment"}
							</button>
							{hasError && (
								<span className="text-sm text-red-500">{fetcher.data?.error}</span>
							)}
							{dailyStatus && !hideDailyLimit && !dailyStatus.isPremium && dailyStatus.loggedIn && (
								<span className="text-xs text-muted-foreground">
									{dailyStatus.remaining} free message{dailyStatus.remaining === 1 ? '' : 's'} remaining today
									{' · '}
									<Link to="/premium" className="text-primary hover:underline">Go Premium</Link>
								</span>
							)}
						</div>
					</fetcher.Form>
				)
			) : currentUserId && isMuted ? (
				<p className="mt-3 text-sm text-muted-foreground">
					You are unable to comment at this time.
				</p>
			) : (
				<p className="mt-3 text-sm text-muted-foreground">
					<Link to="/login" className="underline hover:no-underline">Log in</Link> to leave a comment.
				</p>
			)}

			<div className="mt-4 space-y-4">
				{visibleComments.length === 0 && (
					<p className="text-sm text-muted-foreground">No comments yet. Be the first!</p>
				)}
				{visibleComments.map((comment) => {
					const canDelete =
						currentUserId === comment.user?.id || isAdmin

					return (
						<div
							key={comment.id}
							id={`comment-${comment.id}`}
							className="rounded-lg border border-border bg-muted/30 px-4 py-3"
						>
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2 text-sm">
									{comment.user ? (
										<Link
											to={`/users/${comment.user.username}`}
											className="font-medium hover:underline"
										>
											{comment.user.username}
										</Link>
									) : (
										<span className="font-medium text-muted-foreground">
											Deleted User
										</span>
									)}
									<span className="text-xs text-muted-foreground">
										{formatDistanceToNow(new Date(comment.createdAt), {
											addSuffix: true,
										})}
									</span>
								</div>
								{canDelete && (
									<button
										onClick={() => handleDelete(comment.id)}
										className="text-xs text-muted-foreground hover:text-red-500"
									>
										Delete
									</button>
								)}
							</div>
							<p className="mt-1.5 whitespace-pre-wrap text-sm">
								{comment.text}
							</p>
							<div className="mt-2">
								<CommentReactions
									commentId={comment.id}
									reactions={comment.reactions}
									currentUserId={currentUserId}
								/>
							</div>
						</div>
					)
				})}
				{hasMore && (
					<button
						type="button"
						onClick={() => {
							loadMoreFetcher.load(
								`/resources/comments?contentType=${contentType}&contentId=${contentId}&cursor=${cursor}`,
							)
						}}
						disabled={loadMoreFetcher.state === "loading"}
						className="w-full rounded-lg border border-border py-2 text-sm text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
					>
						{loadMoreFetcher.state === "loading" ? "Loading..." : "Load more comments"}
					</button>
				)}
			</div>
		</div>
	)
}

export default Comments
